'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useState } from 'react';

import { UiKitListCharacteristic } from '@/fo/ui/kit/list/characteristic';
import { UiKitTextCopy } from '@/fo/ui/kit/text/copy';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import {
  CharacteristicOption,
  Characteristics
} from '@/fo/util/map-product-data';

import { styles } from './UiProductInfoCharacteristics.styles';

export interface UiProductInfoCharacteristicsProps {
  characteristics: Characteristics;
}

export function UiProductInfoCharacteristics({
  characteristics: initCharacteristics
}: UiProductInfoCharacteristicsProps) {
  const slots = styles();

  const [characteristics, setCharacteristics] = useState(initCharacteristics);

  const t = useTranslations();

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((product) => {
      const { characteristics } = product;

      setCharacteristics(characteristics);
    });
  }, []);

  const renderOption = (
    option: CharacteristicOption,
    index: number,
    isBrand: boolean
  ) => (
    <Fragment key={`${option.label}-${index}`}>
      {index > 0 && ', '}
      {option.url ? (
        <Link
          href={`/${option.url}`}
          className={isBrand ? slots.brand() : slots.link()}
        >
          {option.label}
        </Link>
      ) : (
        option.label
      )}
    </Fragment>
  );

  const renderValue = (
    value: string,
    url?: string,
    options?: CharacteristicOption[],
    isBrand = false
  ) => {
    if (options?.length) {
      return options.map((option, index) =>
        renderOption(option, index, isBrand)
      );
    }

    if (url) {
      return (
        <Link
          href={`/${url}`}
          className={isBrand ? slots.brand() : slots.link()}
        >
          {value}
        </Link>
      );
    }

    return value;
  };

  return (
    <UiKitListCharacteristic
      className={slots.base()}
      items={characteristics.map(({ label, value, url, options }) => {
        const isBrand = label === t('product.characteristics.brand');

        return {
          label: <p className={slots.label()}>{label}</p>,
          value:
            label === t('product.characteristics.article') ? (
              <UiKitTextCopy text={value} />
            ) : (
              <p className={slots.value()}>
                {renderValue(value, url, options, isBrand)}
              </p>
            )
        };
      })}
    />
  );
}
